<template>
  <AuthLayout>
    <!-- Register -->
        <div class="card">
          <div class="card-body">
            <!-- Logo -->
            <div class="app-brand justify-content-center mb-6">
            </div>
            <!-- /Logo -->
            <h4 class="mb-1">Создание аккаунта 🚀</h4>
            <p class="mb-6">Зарегистрируйтесь для начала работы с Kindata.Profit</p>
            <p v-if="error" class="error">{{ error }}</p>

            <form id="formAuthentication" class="mb-4" @submit.prevent="register">
              <div class="mb-4">
                <label for="name" class="form-label">Имя</label>
                <input
                    v-model="name"
                    type="text"
                    class="form-control"
                    id="name"
                    name="name"
                    placeholder="Ваше имя"
                    required
                    autofocus/>
              </div>
              <div class="mb-4">
                <label for="email" class="form-label">Email</label>
                <input
                    v-model="email"
                    type="email"
                    class="form-control"
                    id="email"
                    name="email"
                    placeholder="Электронная почта"
                    required/>
              </div>
              <div class="mb-4">
                <label for="phone" class="form-label">Телефон</label>
                <input
                    v-model="phone"
                    type="tel"
                    class="form-control"
                    id="phone"
                    name="phone"
                    placeholder="+7XXXXXXXXXX"
                    required/>
                <small v-if="phoneError" class="text-danger">{{ phoneError }}</small>
              </div>
              <div class="mb-4 form-password-toggle">
                <label class="form-label" for="password">Пароль</label>
                <div class="input-group input-group-merge">
                  <input
                      v-model="password"
                      :type="showPassword ? 'text' : 'password'"
                      id="password"
                      class="form-control"
                      name="password"
                      placeholder="&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;"
                      aria-describedby="password"
                      required/>
                  <span class="input-group-text cursor-pointer" @click="togglePassword">
                    <i :class="showPassword ? 'ti ti-eye' : 'ti ti-eye-off'"></i>
                  </span>
                </div>
                <small v-if="passwordError" class="text-danger">{{ passwordError }}</small>
              </div>
              <div class="mb-4">
                <button class="btn btn-primary d-grid w-100" type="submit" :disabled="!isFormValid || isLoading">
                  <span v-if="isLoading">Регистрация...</span>
                  <span v-else>Зарегистрироваться</span>
                </button>
              </div>
            </form>

            <p class="text-center">
              <span>Уже зарегистрированы? </span>
              <RouterLink to="/login">Войти в аккаунт</RouterLink>
            </p>

          </div>
        </div>
        <!-- /Register -->
  </AuthLayout>
</template>

<script>
import { registerUser, getToken } from "@/api/user";
import { auth } from "@/data/auth";
import { user } from "@/data/user";
import { activateTrial } from "@/api/subscription";
import { updateCompanySettings } from "@/api/company";
import router from "@/router";
import AuthLayout from "@/layouts/Auth.vue";

export default {
  name: 'RegisterView',
  components: {AuthLayout},
  data() {
    return {
      name: '',
      email: '',
      phone: '',
      password: '',
      error: '',
      phoneError: '',
      passwordError: '',
      showPassword: false,
      isLoading: false,
      trialSubscriptionId: null,
      isLanding: false
    }
  },
  computed: {
    isFormValid() {
      return this.name.trim() && 
             this.email.trim() && 
             this.isValidPhone && 
             this.isValidPassword;
    },
    isValidPhone() {
      const phoneRegex = /^\+\d{10,15}$/;
      return phoneRegex.test(this.phone);
    },
    isValidPassword() {
      return this.password.length >= 8;
    }
  },
  watch: {
    phone() {
      if (this.phone && !this.isValidPhone) {
        this.phoneError = 'Телефон должен начинаться с + и содержать от 10 до 15 цифр';
      } else {
        this.phoneError = '';
      }
    },
    password() {
      if (this.password && !this.isValidPassword) {
        this.passwordError = 'Пароль должен содержать минимум 8 символов';
      } else {
        this.passwordError = '';
      }
    }
  },
  methods: {
    togglePassword() {
      this.showPassword = !this.showPassword;
    },
    getTimezone() {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    },
    async register() {
      if (!this.isFormValid) return;
      
      this.isLoading = true;
      this.error = '';
      
      try {
        const userData = {
          name: this.name.trim(),
          email: this.email.trim(),
          phone: this.phone.trim(),
          password: this.password,
          with_company: true
        };
        
        await registerUser(userData);
        
        // Успешная регистрация - сразу авторизуем пользователя
        const tokenResult = await getToken(this.email.trim(), this.password);
        auth.setToken(tokenResult.data.access_token, tokenResult.data.refresh_token);
        
        // Часовой пояс компании хранится в Profit, ID при регистрации его не принимает
        try {
          const companies = await user.getUserCompanies();
          if (companies && companies.length) {
            await updateCompanySettings(companies[0].id, {timezone: this.getTimezone()});
          }
        } catch (timezoneError) {
          console.error('Ошибка сохранения часового пояса компании:', timezoneError);
        }
        
        // Если есть trial_subscription в URL, активируем его
        if (this.trialSubscriptionId) {
          try {
            // Получаем активную компанию
            await user.getUserBase();
            const activeCompany = await user.getActiveCompany();
            
            if (activeCompany) {
              await activateTrial(activeCompany, this.trialSubscriptionId);
            }
          } catch (trialError) {
            console.error('Ошибка активации пробного периода:', trialError);
            // Не прерываем процесс регистрации, если не удалось активировать trial
          }
        }
        
        // Перенаправляем в зависимости от параметра landing
        if (this.isLanding) {
          // Если landing=true, перенаправляем на внешнюю страницу успешной регистрации
          window.location.href = 'https://kindata.ru/registration-success/';
        } else {
          // Иначе перенаправляем на главную страницу
          await router.push({ name: 'home' });
        }
        
      } catch (er) {
        this.error = er.response?.data?.detail || 'Произошла ошибка при регистрации';
      } finally {
        this.isLoading = false;
      }
    }
  },
  mounted() {
    // Парсим параметр trial_subscription из URL
    const trialSubscriptionId = this.$route.query.trial_subscription;
    if (trialSubscriptionId) {
      this.trialSubscriptionId = trialSubscriptionId;
    }
    
    // Парсим параметр landing из URL
    if (this.$route.query.landing === 'true') {
      this.isLanding = true;
    }
  }
}
</script>

<style scoped>
.error {
  color: #842029;
  background-color: #f8d7da;
  border: 1px solid #f5c2c7;
  padding: 0.75rem;
  border-radius: 0.375rem;
  margin-bottom: 1rem;
}

.text-danger {
  color: #dc3545 !important;
}
</style> 